import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { useFormik, setNestedObjectValues } from 'formik';
import { Stepper, Container, Step, StepLabel, Typography, Card, CardContent, Button } from '@mui/material';
import Grid from '@mui/material/Grid2';

import { GTag } from '../../../ga';
import { PersonalInfo, Tests, Suppliers, RequestReview, PreferredAssessmentDates } from '../steps';
import { Request, type TestProps } from '../../../models';
import { useAddRequestMutation, usePayRequestMutation } from '../../../slices/apis/requestApi';

const steps = [
	{
		name: 'Candidate Info',
		schema: Request.candidatePersonalInfoSchema,
		defaultValue: Request.candidatePersonalInfoSchema.getDefault(),
	},
	{
		name: 'Preferred Assessment Date',
		schema: Request.preferredAssessmentDateSchema,
		defaultValue: Request.preferredAssessmentDateSchema.getDefault(),
	},
	{
		name: 'Tests',
		schema: Request.testsSchema,
		defaultValue: Request.testsSchema.getDefault(),
	},
	{
		name: 'Testing Centres',
		schema: Request.testingCentreSchema,
		defaultValue: Request.testingCentreSchema.getDefault(),
	},
	{
		name: 'Review',
		schema: Request.reviewSchema,
		defaultValue: Request.reviewSchema.getDefault(),
		resetOnNavBack: true,
	},
];

const NewRequest = () => {
	const [activeStep, setActiveStep] = useState(0);
	const [disableSubmit, setDisableSubmit] = useState(false);
	const { schema } = steps[activeStep];
	const [
		addRequest,
		{
			data: addRequestResponse,
			isSuccess: isAddRequestSuccess,
			isLoading: isAddRequestLoading,
			isError: isAddRequestError,
		},
	] = useAddRequestMutation();
	const [
		payRequest,
		{
			isSuccess: isPayRequestSuccess,
			isLoading: isPayRequestLoading,
			data: payRequestResponse,
			error: payRequestError,
		},
	] = usePayRequestMutation();

	const handleBack = () => {
		if (steps[activeStep].resetOnNavBack) {
			formik.resetForm({
				values: {
					...formik.values,
					...steps[activeStep].defaultValue,
				},
			});
		}

		setActiveStep((currStep) => currStep - 1);
	};

	const formik = useFormik({
		initialValues: Request.schema.getDefault(),
		validationSchema: schema,
		onSubmit: async (values) => {
			await addRequest(values);

			GTag('event', 'form_submit_new_request');
		},
	});

	const onNextClick = async () => {
		const validationErrors = await formik.validateForm();
		if (Object.keys(validationErrors).length > 0) {
			formik.setTouched(setNestedObjectValues(validationErrors, true));
		} else {
			setActiveStep((currStep) => currStep + 1);
		}
	};

	useEffect(() => {
		// disable submit in a hope to prevent duplicated requests being submitted
		if (isAddRequestSuccess) {
			setDisableSubmit(true);
		}

		// disable submit when encounter an submission error
		if (isAddRequestError) {
			setDisableSubmit(true);
		}

		if (isAddRequestSuccess && !isPayRequestLoading && !payRequestError) {
			const { id, tests } = addRequestResponse;

			payRequest({
				requestId: id,
				items: (tests as TestProps[]).map((test) => ({
					id: test.id,
				})),
			});
		}

		if (isPayRequestSuccess && payRequestResponse) {
			GTag('event', 'payment_success_request');

			window.location = payRequestResponse.url;
		}
	}, [
		isAddRequestSuccess,
		isPayRequestSuccess,
		isPayRequestLoading,
		isAddRequestError,
		addRequestResponse,
		payRequest,
		payRequestResponse,
		payRequestError,
	]);

	const formContent = (step: number) => {
		switch (step) {
			case 0:
				return <PersonalInfo formik={formik} />;
			case 1:
				return (
					<PreferredAssessmentDates formik={formik} prefAssessmentDateLabels={Request.preferredAssessmentDateLabels} />
				);
			case 2:
				return <Tests formik={formik} />;
			case 3:
				return <Suppliers formik={formik} />;
			case 4:
				return <RequestReview formik={formik} />;
			default:
				return <div>404: Not Found</div>;
		}
	};

	return (
		<>
			<Helmet>
				<title> New Single Request - {process.env.REACT_APP_ORG} </title>
			</Helmet>
			<Container sx={{ bgcolor: 'rgba(145, 158, 171, 0.12)', paddingY: 3 }} maxWidth="md">
				<Typography variant="h4" align="center">
					New Single Request
				</Typography>
				<form onSubmit={formik.handleSubmit}>
					<Card sx={{ marginTop: 2 }}>
						<CardContent sx={{ p: { xs: 1, md: 5 } }}>
							<Stepper activeStep={activeStep} orientation="horizontal" alternativeLabel>
								{steps.map((step, index) => (
									<Step key={step.name}>
										<StepLabel>{step.name}</StepLabel>
									</Step>
								))}
							</Stepper>
							<Grid container sx={{ my: 2 }}>
								<Grid size={{ md: 12 }}>{formContent(activeStep)}</Grid>
							</Grid>
							<Grid container spacing={4}>
								<Grid>
									<Button disabled={activeStep === 0} onClick={handleBack} type="button">
										Back
									</Button>
								</Grid>
								<Grid>
									{activeStep === steps.length - 1 ? (
										<Button
											loading={isAddRequestLoading || isPayRequestLoading}
											variant="outlined"
											color="brand"
											type="submit"
											disabled={disableSubmit}
											// don't remove key or formik will submit the form even before submit button is clicked
											key="submit"
										>
											<span>Submit</span>
										</Button>
									) : (
										// don't remove key or formik will submit the form even before submit button is clicked
										<Button key="next" variant="outlined" color="primary" onClick={onNextClick} type="button">
											Next
										</Button>
									)}
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</form>
			</Container>
		</>
	);
};

export default NewRequest;
